// DriftFindingsTrend.js — Stacked AreaChart showing vulnerability severity trend
import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import { useThemeColors } from '../hooks/useThemeColors';
import './DriftFindingsTrend.css';

/**
 * Format a date string to MM/DD for the X axis.
 */
function formatAxisDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${m}/${day}`;
}

/**
 * Custom tooltip component styled with CSS classes.
 * Receives Recharts payload — colors come from the Area `stroke` prop (theme-resolved hex).
 */
function CustomTooltip({ active, payload, label }) {
    if (!active || !payload || payload.length === 0) return null;

    return (
        <div className="drift-findings-trend-tooltip">
            <div className="drift-findings-trend-tooltip-date">
                {formatAxisDate(label)}
            </div>
            {payload.map((item) => (
                <div key={item.dataKey} className="drift-findings-trend-tooltip-row">
                    <span
                        className="drift-findings-trend-tooltip-dot"
                        style={{ background: item.color }}
                    />
                    <span className="drift-findings-trend-tooltip-label">{item.dataKey}</span>
                    <span className="drift-findings-trend-tooltip-value">{item.value}</span>
                </div>
            ))}
        </div>
    );
}

function DriftFindingsTrend({ data }) {
    const themeColors = useThemeColors();

    // Sort data ascending by date for proper chart rendering
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];
        return [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [data]);

    if (!data || data.length === 0) {
        return (
            <div className="drift-findings-trend">
                <div className="drift-findings-trend-empty">
                    <p>Not enough scan data to display findings trend.</p>
                </div>
            </div>
        );
    }

    const severityColors = {
        critical: themeColors.severity.critical,
        high: themeColors.severity.high,
        medium: themeColors.severity.medium,
        low: themeColors.severity.low,
    };

    return (
        <div className="drift-findings-trend">
            <h4 className="drift-findings-trend-header">Findings Trend</h4>
            <div className="drift-findings-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                        <defs>
                            {Object.entries(severityColors).map(([key, color]) => (
                                <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={themeColors.border.dark}
                            vertical={false}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatAxisDate}
                            tick={{ fill: themeColors.text.muted, fontSize: 11 }}
                            stroke={themeColors.border.dark}
                            tickLine={false}
                        />
                        <YAxis
                            allowDecimals={false}
                            tick={{ fill: themeColors.text.muted, fontSize: 11 }}
                            stroke={themeColors.border.dark}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{ fontSize: '0.7rem', paddingTop: '8px' }}
                            iconType="circle"
                            iconSize={8}
                        />
                        <Area
                            type="monotone"
                            dataKey="critical"
                            stackId="severity"
                            stroke={severityColors.critical}
                            fill={`url(#grad-critical)`}
                            fillOpacity={1}
                        />
                        <Area
                            type="monotone"
                            dataKey="high"
                            stackId="severity"
                            stroke={severityColors.high}
                            fill={`url(#grad-high)`}
                            fillOpacity={1}
                        />
                        <Area
                            type="monotone"
                            dataKey="medium"
                            stackId="severity"
                            stroke={severityColors.medium}
                            fill={`url(#grad-medium)`}
                            fillOpacity={1}
                        />
                        <Area
                            type="monotone"
                            dataKey="low"
                            stackId="severity"
                            stroke={severityColors.low}
                            fill={`url(#grad-low)`}
                            fillOpacity={1}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default DriftFindingsTrend;
